/**
 * Date display utilities
 *
 * Problem: `new Date('2026-09-05')` parses the date-only string as UTC midnight
 * (per the ECMAScript spec), then `.toLocaleDateString()` converts it to local
 * time — showing Sep 4 for users in UTC-1 through UTC-12.  The fix is to force
 * the parser to treat the date as local noon, which is always the same calendar
 * day regardless of timezone offset.
 *
 * Rule of thumb:
 *   - Date-only string from DB (YYYY-MM-DD) → use parseLocalDate / formatDateDisplay
 *   - Full ISO timestamp (has 'T') → new Date(ts) is fine, already in UTC with offset
 */

/**
 * Parse a date-only string (YYYY-MM-DD) as local noon to avoid timezone shift.
 * If the string already contains 'T' it is a full ISO timestamp and is returned
 * to `new Date()` as-is.
 */
export function parseLocalDate(dateStr: string): Date {
  return new Date(dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00');
}

/**
 * Format a date-only string (YYYY-MM-DD) for display without timezone shift.
 * Accepts the same `options` as `Intl.DateTimeFormat`.
 *
 * Default format: "Sep 5, 2026"
 */
export function formatDateDisplay(
  dateStr: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale = 'en-US',
): string {
  return parseLocalDate(dateStr).toLocaleDateString(locale, options);
}
